import { useState, useEffect, useRef, useCallback } from 'react'

interface UsePollingOptions<T> {
  fetcher: () => Promise<T>
  interval: number
}

interface UsePollingReturn<T> {
  data: T | null
  loading: boolean
  error: string | null
  isRefreshing: boolean
}

/**
 * A reusable polling hook that fetches data at regular intervals.
 * Implements exponential backoff on consecutive errors (doubles interval up to 4x base).
 * Resets backoff on successful fetch.
 * Properly cancels polling on unmount — no leaked requests.
 *
 * @param fetcher - Async function that returns the data to poll
 * @param interval - Base polling interval in milliseconds
 * @returns Object containing data, loading states, and error
 */
export default function usePolling<T>({
  fetcher,
  interval,
}: UsePollingOptions<T>): UsePollingReturn<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Use refs to avoid stale closures and dependency churn
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const consecutiveErrorsRef = useRef(0)
  const timeoutRef = useRef<number | null>(null)
  const cancelledRef = useRef(false)
  const hasDataRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    hasDataRef.current = false

    const doFetch = async () => {
      if (cancelledRef.current) return

      if (!hasDataRef.current) {
        setLoading(true)
      } else {
        setIsRefreshing(true)
      }

      try {
        const result = await fetcherRef.current()
        if (cancelledRef.current) return
        setData(result)
        setError(null)
        hasDataRef.current = true
        consecutiveErrorsRef.current = 0
      } catch (err) {
        if (cancelledRef.current) return
        const errorMessage = err instanceof Error ? err.message : 'An error occurred'
        setError(errorMessage)
        consecutiveErrorsRef.current += 1
      } finally {
        if (!cancelledRef.current) {
          setLoading(false)
          setIsRefreshing(false)
        }
      }
    }

    const scheduleNext = () => {
      if (cancelledRef.current) return

      const backoffMultiplier = Math.min(
        Math.pow(2, consecutiveErrorsRef.current),
        4
      )
      const nextInterval = interval * backoffMultiplier

      timeoutRef.current = window.setTimeout(async () => {
        await doFetch()
        scheduleNext()
      }, nextInterval)
    }

    // Initial fetch, then start polling
    doFetch().then(scheduleNext)

    // Cleanup — cancel flag prevents scheduling after unmount
    return () => {
      cancelledRef.current = true
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [interval])

  return {
    data,
    loading,
    error,
    isRefreshing,
  }
}
